import * as React from "react";
import classnames from "classnames";
import { isNumber } from "../app/utils/assert";

export enum BrushDimension
{
    X,
    Y,
    XY
}

type HandleType = 'n' | 'e' | 's' | 'w' | 'nw' | 'ne' | 'se' | 'sw';
type Handle = { type: HandleType };
interface Dimension
{
    handles: Handle[];
}

const type = (h: HandleType) => ({ type: h });
const X: Dimension = {
    handles: ['e', 'w'].map(type)
};
const Y: Dimension = {
    handles: ['n', 's'].map(type),
};
const XY: Dimension = {
    handles: ['n', 'e', 's', 'w', 'nw', 'ne', 'se', 'sw'].map(type),
};

const mapDimension = (dimType: BrushDimension) => {
    switch(dimType) {
        case BrushDimension.X:
            return X;
        case BrushDimension.Y:
            return Y;
        case BrushDimension.XY:
            return XY;
        default:
            throw new Error('Invalid program error');
    }
}

const cursors = {
    overlay: 'crosshair',
    selection: 'move',
    n: 'ns-resize',
    e: 'ew-resize',
    s: 'ns-resize',
    w: 'ew-resize',
    nw: 'nwse-resize',
    ne: 'nesw-resize',
    se: 'nwse-resize',
    sw: 'nesw-resize'
};

const signsX = {
    overlay: +1,
    selection: +1,
    n: null,
    e: +1,
    s: null,
    w: -1,
    nw: -1,
    ne: +1,
    se: +1,
    sw: -1
};
  
const signsY = {
    overlay: +1,
    selection: +1,
    n: -1,
    e: null,
    s: +1,
    w: null,
    nw: -1,
    ne: -1,
    se: +1,
    sw: +1
};

enum Mode
{
    Handle,
    Drag
}

const handleSize = 6;

type BrushEventHandler = (x0: number, y0: number, x1: number, y1: number) => void;
interface Props extends React.SVGAttributes<SVGElement>
{
    // extent
    x0: number;
    y0: number;
    x1: number;
    y1: number;

    // selection
    w?: number;
    n?: number;
    e?: number;
    s?: number;

    dimension?: BrushDimension;
    onStart?: BrushEventHandler;
    onBrush?: BrushEventHandler;
    onEnd?: BrushEventHandler;
}

interface State
{
    // selection
    w: number;
    n: number;
    e: number;
    s: number;

    drag: {
        mode: Mode;
        
        x: number;
        y: number;

        signX: number;
        signY: number;
    };
}

// This is a Rect reimplementation of d3's Brush component
// https://github.com/d3/d3-brush
export class Brush extends React.Component<Props, State>
{
    public static defaultProps: Partial<Props> = {
        dimension: BrushDimension.XY
    };

    private overlay: SVGElement;

    constructor(props: Props)
    {
        super(props);
        let { x0, y0, x1, y1, w = x0, n = y0, e = x1, s = y1 } = props;
        this.state = {
            w, n, e, s,
            drag: null,            
        };
    }

    componentWillReceiveProps(nextProps: Readonly<Props>, nextContext: any)
    {
        const { w, n, e, s } = nextProps;
        const { w: prev_w, n: prev_n, e: prev_e, s: prev_s } = this.state;
        this.setState({
            w: isNumber(w) ? w : prev_w,
            n: isNumber(n) ? n : prev_n,
            e: isNumber(e) ? e : prev_e,
            s: isNumber(s) ? s : prev_s
        });
    }

    render()
    {
        const {
            x0, y0, x1, y1,
            w: _w, n: _n, e: _e, s: _s, // ignore
            dimension,
            className,
            onStart, onBrush, onEnd,
            ...gProps
        } = this.props;
        const {
            w, n, e, s
        } = this.state;

        const dim = mapDimension(dimension);
        return (
            <g className={classnames('brush', className)} fill="none" style={{WebkitTapHighlightColor: 'rgba(0,0,0,0)'}} pointerEvents="all" {...gProps} >
                <rect className="overlay" pointerEvents="all" cursor={cursors.overlay} x={Math.min(x0, x1)} y={Math.min(y0, y1)} width={Math.abs(x1 - x0)} height={Math.abs(y1 - y0)} ref={e => this.overlay = e} />
                <rect className="selection" cursor={cursors.selection} fill="#777" fillOpacity={0.3} stroke="#fff" shapeRendering="crispEdges"
                    x={w} y={n} width={Math.abs(e-w)} height={Math.abs(s-n)}
                    onMouseDown={this.onSelectionMouseDown}
                />
                { 
                    dim.handles.map((h, idx) => (
                        <rect key={idx} className={`handle handle--${h.type}`} cursor={cursors[h.type]}
                            x={ (h.type.endsWith('e') ? e : w) - handleSize / 2 }
                            y={ (h.type.startsWith('s') ? s : n) - handleSize / 2 }
                            width={ h.type === 'n' || h.type === 's' ? Math.abs(e - w) + handleSize : handleSize }
                            height={ h.type === 'w' || h.type === 'e' ? Math.abs(s - n) + handleSize : handleSize }
                            onMouseDown={e => this.onHandleMouseDown(e, h)}
                        />
                    ))
                }
            </g>
        );

    }

    private onHandleMouseDown = (e: React.MouseEvent<SVGElement>, handle: Handle) => this.handleMouseDown(e, Mode.Handle, signsX[handle.type], signsY[handle.type]);
    private onSelectionMouseDown = (e: React.MouseEvent<SVGElement>) => {
        const { dimension } = this.props;
        const signX = (dimension === BrushDimension.Y) ? null : signsX['selection'];
        const signY = (dimension === BrushDimension.X) ? null : signsY['selection'];
        this.handleMouseDown(e, Mode.Drag, signX, signY);
    }
    private handleMouseDown = (e: React.MouseEvent<SVGElement>, mode: Mode, signX: number, signY: number) => {
        // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
        if(e.button) return;
        
        document.addEventListener('mouseup', this.onMouseUp , true);
        document.addEventListener('mousemove', this.onMouseMove, true);

        let { x, y } = this.point(e);
        this.setState({ drag: { x, y, mode, signX, signY } }, () => this.raise(this.props.onStart));        
    }


    private onMouseMove = (evt: MouseEvent) => {
        evt.preventDefault();

        const { clientX, clientY } = evt;
        let { drag: { mode, x, y, signX, signY }, w, n, e, s } = this.state;
        const { dimension, x0: W, y0: N, x1: E, y1: S } = this.props;
        
        let { x: x1, y: y1 } = this.point(evt);
        let dx = x1 - x;
        let dy = y1 - y;
        
        let w1 = w, n1 = n, e1 = e, s1 = s;

        switch(mode) {
            case Mode.Handle:
                if(signX < 0) {
                    w1 = Math.max(W, Math.min(E, w + dx));
                    e1 = e;
                } else if(signX > 0) {
                    e1 = Math.min(E, Math.max(W, e + dx));
                    w1 = w;
                }
        
                if(signY < 0) {
                    n1 = Math.max(N, Math.min(S, n + dy));
                    s1 = s;
                } else if(signY > 0) {
                    s1 = Math.min(S, Math.max(N, s + dy));
                    n1 = n;
                }    
            break;
            
            case Mode.Drag:
                if(signX) {
                    dx = Math.max(W - w, Math.min(E - e, dx));
                    w1 = w + dx;
                    e1 = e + dx;
                }
                if(signY) {
                    dy = Math.max(N - n, Math.min(S - s));
                    n1 = n + dy;
                    s1 = s + dy;
                }
            break;
            
            default:
                throw new Error('Not implemented');
        }

        let t: number;
        if(e1 < w1) {
            signX *= -1;
            t = e1; e1 = w1, w1 = t;
        }
        if(s1 < n1) {
            signY *= -1;
            t = s1, s1 = n1, n1 = t;
        }
        
        if(w !== w1 || n !== n1 || e !== e1 || s !== s1) {
            this.setState({
                w: w1, n: n1, e: e1, s: s1,
                drag: {
                    mode,
                    x: x1,
                    y: y1,
                    signX, signY,
                }
            }, () => this.raise(this.props.onBrush));
        }
    }

    private onMouseUp = (evt: MouseEvent) => {
        document.removeEventListener('mouseup', this.onMouseUp, true);
        document.removeEventListener('mousemove', this.onMouseMove, true);
        this.setState({ drag: null }, () => this.raise(this.props.onEnd));
    }

    private point(e: React.MouseEvent<any> | MouseEvent)
    {
        const { x0, y0, x1, y1 } = this.props;
        const toSVGCoordinateSystem = (this.overlay as SVGGraphicsElement).getScreenCTM().inverse();
        let { x, y } = mult(toSVGCoordinateSystem, { x: e.clientX, y: e.clientY });
        x = Math.max(x0, Math.min(x1, x));
        y = Math.max(y0, Math.min(y1, y));
        return { x, y };
    }

    private raise = (handler: BrushEventHandler) => {
        if(!handler) return;
        const { w, n, e, s } = this.state;
        handler(w, n, e, s);
    }
}


function mult(transform: SVGMatrix, p: {x: number, y: number})
{
    return {
        x: transform.a*p.x + transform.c*p.y + transform.e,
        y: transform.b*p.x + transform.d*p.y + transform.f 
    };
}

// Drag feature explained
// https://stackoverflow.com/questions/30231880/setcapture-and-releasecapture-in-chrome
// https://stackoverflow.com/questions/9334084/moveable-draggable-div


// WEBPACK FOOTER //
// ./src/d3chart/Brush.tsx